import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { DataLayer } from '@grandvisionhq/analytics';
import { isSafePath } from '@grandvisionhq/frk-routing';
import { useIntl } from '@grandvisionhq/state';
import { useDispatchTrackEvent, useTrackPageView } from '@grandvisionhq/svc-tealium/events';
import { cancelAppointmentScreenEvent } from '../../../../data-layer/actions.js';
import { fromArrayToString } from '../../../../utils/from-array-to-string.js';
import '@grandvisionhq/graphql';
import '@grandvisionhq/utils';
import '../../../BookAppointment/queries/get-book-appointment-store-by-id.js';
import '../../../BookAppointment/queries/get-book-appointment-type.js';
import '@grandvisionhq/bloomreach';
import useVirtualAppointment from '../../../BookAppointment/hooks/use-virtual-appointment.js';
import { useCancelAppointmentContext } from '../../context/context.js';
import { useRoutingContext } from '../../context/RoutingContext/context.js';
import useAppointmentById from '../../hooks/api/use-appointment-by-id.js';
import useAppointmentByToken from '../../hooks/api/use-appointment-by-token.js';
import useCancelAppointmentById from '../../hooks/api/use-cancel-appointment-by-id.js';
import useCancelAppointmentByToken from '../../hooks/api/use-cancel-appointment-by-token.js';
import '../../queries/cancel-appointment-by-token.js';
import '../../queries/cancel-appointment-by-id.js';
import '../../queries/get-appointment-by-id.js';
import '../../queries/get-appointment-by-token.js';
import '../../queries/get-store-by-code.js';
import '../../queries/get-store-by-id.js';
import useStoreById from '../../hooks/api/use-store-by-id.js';
import CancelAppointment from './CancelAppointment.js';

const INTERNAL_SERVER_ERROR_STATUS = 'INTERNAL_SERVER_ERROR';
const CancelAppointmentContainer = ({ userToken , appointmentId , token  })=>{
    const { appointment , setAppointment , setStore  } = useCancelAppointmentContext();
    const [{ goTo  }, { queryParams  }] = useRoutingContext();
    const [errorCode, setErrorCode] = React.useState('');
    const { getLabel , getFormattedLabel  } = useIntl();
    const labels = React.useMemo(()=>{
        var ref;
        return {
            title: getFormattedLabel('appointments.page.cancel.title', {
                labelData: {
                    name: (appointment === null || appointment === void 0 ? void 0 : (ref = appointment.invitee) === null || ref === void 0 ? void 0 : ref.firstName) || ''
                }
            }),
            subtitle: getLabel('appointments.page.cancel.subtitle'),
            reason: getLabel('appointments.page.cancel.reason'),
            cancelButton: getLabel('appointments.page.cancel.buttonLabel.cancel'),
            closeButton: getLabel('appointments.page.cancel.buttonLabel.close'),
            textareaLabel: getLabel('appointments.page.cancel.textareaLabel'),
            errorValidation: getLabel('appointments.page.cancel.errorValidation'),
            errorTitle: getLabel('appointments.page.cancel.errorTitle'),
            errorMessage: getLabel(`appointments.page.cancel.error.${errorCode.toLowerCase()}`, {
                hideLabelKey: true
            }),
            slowLoadingIndication: getLabel('appointments.page.cancel.slowLoadingIndication')
        };
    }, [
        appointment,
        errorCode,
        getLabel,
        getFormattedLabel
    ]);
    const [getAppointmentById, { data: appointmentById , loading: appointmentByIdLoading , error: appointmentByIdError  }, ] = useAppointmentById({
        id: appointmentId !== null && appointmentId !== void 0 ? appointmentId : ''
    }, userToken !== null && userToken !== void 0 ? userToken : '');
    const [getAppointmentByToken, { data: appointmentByToken , loading: appointmentByTokenLoading , error: appointmentByTokenError ,  }, ] = useAppointmentByToken({
        token: token !== null && token !== void 0 ? token : ''
    });
    const [cancelAppointmentByToken, { data: cancelAppointmentByTokenData , loading: cancelAppointmentByTokenDataLoading , error: cancelAppointmentByTokenError ,  }, ] = useCancelAppointmentByToken();
    const [cancelAppointmentById, { data: cancelAppointmentByIdData , loading: cancelAppointmentByIdDataLoading , error: cancelAppointmentByIdError ,  }, ] = useCancelAppointmentById(userToken !== null && userToken !== void 0 ? userToken : '');
    const [{ isVirtualAppointmentType  }] = useVirtualAppointment();
    const [getStoreById, { data: store , loading: storeLoading  }] = useStoreById({
        globalStoreId: '0'
    });
    const aabCancellationEvent = React.useCallback(()=>Promise.resolve({
            id: 'Event',
            Events_FunnelCancellation: '1'
        })
    , []);
    const dispatchTrackEvent = useDispatchTrackEvent();
    const onSubmit = React.useCallback((cancellationReason)=>{
        setErrorCode('');
        if (token) {
            cancelAppointmentByToken({
                variables: swcHelpers.objectSpread({
                    token
                }, cancellationReason ? {
                    cancellationReason
                } : {})
            });
        }
        if (appointmentId) {
            cancelAppointmentById({
                variables: swcHelpers.objectSpread({
                    id: appointmentId
                }, cancellationReason ? {
                    cancellationReason
                } : {})
            });
        }
        dispatchTrackEvent(aabCancellationEvent);
    }, [
        token,
        appointmentId,
        cancelAppointmentByToken,
        cancelAppointmentById
    ]);
    React.useEffect(()=>{
        if (appointment && store) {
            var ref, ref2;
            var ref3;
            const dataLayerEvent = cancelAppointmentScreenEvent({
                appointment: {
                    startTime: appointment.startTime,
                    endTime: appointment.endTime,
                    appointmentType: {
                        id: ((ref = appointment.appointmentType) === null || ref === void 0 ? void 0 : ref.id) || '',
                        ageGroup: (ref3 = (ref2 = appointment.appointmentType) === null || ref2 === void 0 ? void 0 : ref2.ageGroup) !== null && ref3 !== void 0 ? ref3 : 'ADULT'
                    }
                },
                store: store
            });
            DataLayer.pushEvent(dataLayerEvent);
        }
    }, [
        appointment,
        store
    ]);
    React.useEffect(()=>{
        if (!cancelAppointmentByTokenData) {
            return;
        }
        goTo('confirmation', queryParams);
    }, [
        cancelAppointmentByTokenData,
        queryParams,
        goTo
    ]);
    React.useEffect(()=>{
        if (!cancelAppointmentByIdData) {
            return;
        }
        goTo('confirmation', queryParams);
    }, [
        cancelAppointmentByIdData,
        queryParams,
        goTo
    ]);
    React.useEffect(()=>{
        if (appointmentId && userToken) {
            getAppointmentById({
                variables: {
                    id: appointmentId
                }
            });
        }
    }, [
        appointmentId,
        userToken
    ]);
    React.useEffect(()=>{
        if (token) {
            getAppointmentByToken({
                variables: {
                    token
                }
            });
        }
    }, [
        token
    ]);
    React.useEffect(()=>{
        if (!appointmentById) {
            return;
        }
        setAppointment(appointmentById);
    }, [
        appointmentById
    ]);
    React.useEffect(()=>{
        if (!appointmentByToken) {
            return;
        }
        setAppointment(appointmentByToken);
    }, [
        appointmentByToken
    ]);
    React.useEffect(()=>{
        if (appointmentByIdError) {
            var ref, ref4, ref5;
            var ref6;
            throw new Error((ref6 = (ref = appointmentByIdError.graphQLErrors) === null || ref === void 0 ? void 0 : (ref4 = ref[0]) === null || ref4 === void 0 ? void 0 : (ref5 = ref4.extensions) === null || ref5 === void 0 ? void 0 : ref5.code) !== null && ref6 !== void 0 ? ref6 : INTERNAL_SERVER_ERROR_STATUS);
        }
        if (appointmentByTokenError) {
            var ref7, ref8, ref9;
            var ref10;
            throw new Error((ref10 = appointmentByTokenError === null || appointmentByTokenError === void 0 ? void 0 : (ref7 = appointmentByTokenError.graphQLErrors) === null || ref7 === void 0 ? void 0 : (ref8 = ref7[0]) === null || ref8 === void 0 ? void 0 : (ref9 = ref8.extensions) === null || ref9 === void 0 ? void 0 : ref9.code) !== null && ref10 !== void 0 ? ref10 : INTERNAL_SERVER_ERROR_STATUS);
        }
    }, [
        appointmentByIdError,
        appointmentByTokenError
    ]);
    React.useEffect(()=>{
        if (!store) {
            return;
        }
        setStore(store);
    }, [
        store
    ]);
    React.useEffect(()=>{
        var ref;
        const isVirtualAppointment = isVirtualAppointmentType(appointment === null || appointment === void 0 ? void 0 : appointment.appointmentType);
        const globalStoreId = isVirtualAppointment ? undefined : appointment === null || appointment === void 0 ? void 0 : (ref = appointment.storeInfo) === null || ref === void 0 ? void 0 : ref.globalStoreId;
        if (globalStoreId) {
            getStoreById({
                variables: {
                    globalStoreId
                }
            });
        }
    }, [
        appointment
    ]);
    React.useEffect(()=>{
        if (cancelAppointmentByTokenError) {
            var ref, ref11, ref12;
            var ref13;
            setErrorCode((ref13 = cancelAppointmentByTokenError === null || cancelAppointmentByTokenError === void 0 ? void 0 : (ref = cancelAppointmentByTokenError.graphQLErrors) === null || ref === void 0 ? void 0 : (ref11 = ref[0]) === null || ref11 === void 0 ? void 0 : (ref12 = ref11.extensions) === null || ref12 === void 0 ? void 0 : ref12.code) !== null && ref13 !== void 0 ? ref13 : INTERNAL_SERVER_ERROR_STATUS);
        }
        if (cancelAppointmentByIdError) {
            var ref14, ref15, ref16;
            var ref17;
            setErrorCode((ref17 = cancelAppointmentByIdError === null || cancelAppointmentByIdError === void 0 ? void 0 : (ref14 = cancelAppointmentByIdError.graphQLErrors) === null || ref14 === void 0 ? void 0 : (ref15 = ref14[0]) === null || ref15 === void 0 ? void 0 : (ref16 = ref15.extensions) === null || ref16 === void 0 ? void 0 : ref16.code) !== null && ref17 !== void 0 ? ref17 : INTERNAL_SERVER_ERROR_STATUS);
        }
    }, [
        cancelAppointmentByIdError,
        cancelAppointmentByTokenError
    ]);
    const aabCancellationPageView = React.useCallback(()=>{
        return new Promise((resolve)=>{
            if (!appointment || !store) {
                return;
            }
            resolve({
                id: 'VirtualPage-View',
                Page_Type: 'Cancellation',
                Page_Section1: 'Exam'
            });
        });
    }, [
        appointment,
        store
    ]);
    useTrackPageView(aabCancellationPageView);
    var ref1;
    const rawReturnPath = (ref1 = fromArrayToString(queryParams.returnPath)) !== null && ref1 !== void 0 ? ref1 : '/';
    const safeReturnPath = isSafePath(rawReturnPath) ? rawReturnPath : '/';
    return /*#__PURE__*/ React.createElement(CancelAppointment, {
        appointment: appointment !== null && appointment !== void 0 ? appointment : undefined,
        store: store !== null && store !== void 0 ? store : undefined,
        isAppointmentLoading: appointmentByIdLoading || appointmentByTokenLoading,
        isStoreLoading: storeLoading,
        labels: labels,
        returnPath: safeReturnPath,
        onSubmit: onSubmit,
        isCancellationInProgress: cancelAppointmentByTokenDataLoading || cancelAppointmentByIdDataLoading,
        hasError: !!errorCode,
        disableCancelAppointmentButton: !!errorCode && errorCode !== INTERNAL_SERVER_ERROR_STATUS
    });
};

export { CancelAppointmentContainer as default };
//# sourceMappingURL=CancelAppointmentContainer.js.map
